import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, X, Send, Loader2 } from 'lucide-react';
import BrandLogo from './BrandLogo';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { isNativeApp } from '../utils/photoCapture';
import { API } from '../lib/api';

const QUESTIONS = [
  {
    key: 'stuck_with',
    prompt: 'What were you stuck with, if anything?',
    placeholder: 'e.g. I couldn\u2019t figure out how to add a second room\u2026',
  },
  {
    key: 'must_have',
    prompt: 'What must-have feature would make your process easier?',
    placeholder: 'e.g. Bulk photo upload, tenant e-signatures\u2026',
  },
  {
    key: 'not_needed',
    prompt: 'What felt unnecessary or got in your way?',
    placeholder: 'e.g. Too many confirmation steps when\u2026',
  },
];

function deviceClass() {
  if (isNativeApp()) return 'native';
  return window.innerWidth < 768 ? 'mobile-web' : 'desktop';
}

function dismissKey(inspectionId) {
  return `realspect_fb_prompt_dismissed_${inspectionId || 'global'}`;
}

export default function UserFeedbackChat() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(null); // { trigger, inspectionId }
  const [step, setStep] = useState(0); // 0 = rating, 1..3 = questions, 4 = done
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [answers, setAnswers] = useState({ stuck_with: '', must_have: '', not_needed: '' });
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const triggerRef = useRef({ trigger: 'manual', inspectionId: null });
  const bodyRef = useRef(null);

  // Contextual trigger: pages dispatch this event after a completed analysis
  // or inspection. One non-blocking prompt, remembered per inspection.
  useEffect(() => {
    const handler = (e) => {
      const { trigger, inspectionId } = e.detail || {};
      if (localStorage.getItem(dismissKey(inspectionId))) return;
      setPrompt({ trigger: trigger || 'post_analysis', inspectionId: inspectionId || null });
    };
    window.addEventListener('realspect:feedback-prompt', handler);
    return () => window.removeEventListener('realspect:feedback-prompt', handler);
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [step, open]);

  const resetChat = useCallback(() => {
    setStep(0);
    setRating(0);
    setHoverRating(0);
    setAnswers({ stuck_with: '', must_have: '', not_needed: '' });
    setDraft('');
  }, []);

  const openChat = (trigger = 'manual', inspectionId = null) => {
    triggerRef.current = { trigger, inspectionId };
    resetChat();
    setOpen(true);
    setPrompt(null);
    if (inspectionId) localStorage.setItem(dismissKey(inspectionId), '1');
  };

  const dismissPrompt = () => {
    if (prompt?.inspectionId) localStorage.setItem(dismissKey(prompt.inspectionId), '1');
    setPrompt(null);
  };

  const pickRating = (value) => {
    setRating(value);
    setStep(1);
    setDraft('');
  };

  const nextQuestion = (skip = false) => {
    const q = QUESTIONS[step - 1];
    const value = skip ? '' : draft.trim();
    const merged = { ...answers, [q.key]: value };
    setAnswers(merged);
    setDraft('');
    if (step < QUESTIONS.length) {
      setStep(step + 1);
    } else {
      submit(merged);
    }
  };

  const submit = async (finalAnswers) => {
    setSaving(true);
    try {
      await axios.post(`${API}/user-feedback`, {
        rating,
        ...finalAnswers,
        inspection_id: triggerRef.current.inspectionId,
        route: location.pathname,
        trigger_source: triggerRef.current.trigger,
        metadata: {
          device_class: deviceClass(),
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          user_agent_brand: navigator.userAgentData?.platform || navigator.platform || '',
        },
      });
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send feedback — please try again');
    } finally {
      setSaving(false);
    }
  };

  const hearts = (
    <div className="flex items-center gap-1.5" data-testid="feedback-hearts" role="group" aria-label="Rate your experience from 1 to 5">
      {[1, 2, 3, 4, 5].map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => pickRating(v)}
          onMouseEnter={() => setHoverRating(v)}
          onMouseLeave={() => setHoverRating(0)}
          aria-label={`Rate ${v} of 5`}
          aria-pressed={rating === v}
          data-testid={`feedback-heart-${v}`}
          className={`h-11 w-11 rounded-md border font-mono text-base font-bold transition-colors ${
            v === (hoverRating || rating)
              ? 'border-marker bg-marker text-on-marker'
              : 'border-line-strong bg-surface text-ink hover:bg-sunken'
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  );

  const botBubble = (text, key) => (
    <div key={key} className="flex items-start gap-2">
      <BrandLogo className="h-7 w-7" />
      <div className="max-w-[85%] rounded-md bg-sunken px-3.5 py-2.5 text-[15px] leading-[22px] text-ink">{text}</div>
    </div>
  );

  const userBubble = (text, key) => (
    <div key={key} className="flex justify-end">
      <div className="max-w-[85%] whitespace-pre-wrap rounded-md bg-marker-soft px-3.5 py-2.5 text-[15px] leading-[22px] text-ink">{text}</div>
    </div>
  );

  const transcript = [];
  transcript.push(botBubble('Thanks for using RealSpect — how was your experience?', 'q-rating'));
  if (step >= 1) {
    transcript.push(userBubble(`${rating} out of 5`, 'a-rating'));
  }
  QUESTIONS.forEach((q, i) => {
    if (step >= i + 1) transcript.push(botBubble(q.prompt, `q-${q.key}`));
    if (step >= i + 2) {
      transcript.push(userBubble(answers[q.key] || '(skipped)', `a-${q.key}`));
    }
  });
  if (step === 4) {
    transcript.push(botBubble('Got it — thank you! Every answer directly shapes what we build next.', 'q-done'));
  }

  return (
    <>
      {/* Non-blocking post-analysis prompt */}
      {prompt && !open && (
        <div className="fixed bottom-20 right-4 z-[60] max-w-[300px] rounded-md border border-line bg-surface p-4 text-ink shadow-overlay" data-testid="feedback-prompt">
          <div className="flex items-start justify-between gap-2">
            <p className="m-0 text-[15px] font-bold leading-[22px]">How did that go? Tell us in 30 seconds. It shapes what we build next.</p>
            <button type="button" onClick={dismissPrompt} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-sunken" aria-label="Dismiss feedback prompt">
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
          <Button
            onClick={() => openChat(prompt.trigger, prompt.inspectionId)}
            size="sm"
            className="mt-3 w-full"
            data-testid="feedback-prompt-open"
          >
            Give quick feedback
          </Button>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-20 right-4 z-[60] flex w-[min(92vw,360px)] flex-col overflow-hidden rounded-lg border border-line bg-surface text-ink shadow-overlay" role="dialog" aria-label="Feedback" data-testid="feedback-chat">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <p className="m-0 text-[15px] font-bold">Help us improve RealSpect</p>
            <button type="button" onClick={() => setOpen(false)} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-sunken" aria-label="Close feedback chat" data-testid="feedback-chat-close">
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>

          <div ref={bodyRef} className="p-4 space-y-3 max-h-[45vh] overflow-y-auto">
            {transcript}
            {step === 0 && <div className="pl-9">{hearts}</div>}
          </div>

          {step >= 1 && step <= QUESTIONS.length && (
            <div className="flex flex-col gap-2 border-t border-line p-3">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={QUESTIONS[step - 1].placeholder}
                aria-label={QUESTIONS[step - 1].prompt}
                className="min-h-[80px]"
                maxLength={2000}
                data-testid="feedback-answer-input"
              />
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm font-semibold text-ink-muted">Step {step + 1} of {QUESTIONS.length + 1}</span>
                <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => nextQuestion(true)}
                  disabled={saving}
                  data-testid="feedback-skip"
                >
                  Skip
                </Button>
                <Button
                  onClick={() => nextQuestion(false)}
                  disabled={saving || !draft.trim()}
                  size="sm"
                  data-testid="feedback-next"
                >
                  {saving ? <Loader2 className="animate-spin" /> : (
                    <>
                      {step === QUESTIONS.length ? 'Send' : 'Next'} <Send strokeWidth={1.5} />
                    </>
                  )}
                </Button>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="border-t border-line p-3">
              <Button onClick={() => setOpen(false)} variant="outline" size="sm" className="w-full">
                Close
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Persistent floating button */}
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openChat('manual', null))}
        className="fixed bottom-4 right-4 z-[60] flex h-12 w-12 items-center justify-center rounded-md bg-ink text-paper shadow-overlay transition-opacity hover:opacity-90"
        aria-label="Send feedback"
        aria-expanded={open}
        data-testid="feedback-fab"
      >
        <MessageSquare className="h-6 w-6" strokeWidth={1.5} />
      </button>
    </>
  );
}
